import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Tooltip,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Loader,
  Center,
  Select,
  Pill,
  Flex,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconCheck,
  IconX,
  IconFilter,
  IconRefresh,
  IconChevronRight,
  IconChevronDown,
} from "@tabler/icons-react";
import {
  useGroupsAdmin,
  useCreateGroup,
  useUpdateGroup,
  useToggleGroupActive,
} from "../../../hooks/admin/useGroups";
import {
  useGroupPermissionsByGroupId,
  useCreateGroupPermission,
  useDeleteGroupPermission,
} from "../../../hooks/admin/useGroupPermissions";
import { usePermissionsAdmin } from "../../../hooks/admin/usePermissions";
import GroupForm, {
  GroupFormValues,
} from "../../../components/admin/groups/GroupForm";
import { Group as GroupType } from "../../../services/admin/groups.service";
import { formatPermissionLabel } from "../../../utils/formatters";

const ITEMS_PER_PAGE = 20;

interface GroupFilters {
  status?: "all" | "active" | "inactive";
}

function GroupPermissionsRow({ groupId }: { groupId: string }) {
  const { data: groupPermissions = [], isLoading } =
    useGroupPermissionsByGroupId(groupId);
  const { data: allPermissions = [] } = usePermissionsAdmin();
  const createGroupPermission = useCreateGroupPermission();
  const deleteGroupPermission = useDeleteGroupPermission();
  const [addOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false);
  const [selectedPermissionId, setSelectedPermissionId] = useState<
    string | null
  >(null);
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] =
    useDisclosure(false);
  const [permToRemove, setPermToRemove] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const assignedPermissionIds = new Set(
    groupPermissions.map((gp) => gp.permissionId)
  );

  const availablePermissions = allPermissions
    .filter((p) => !assignedPermissionIds.has(p.id))
    .map((p) => ({
      value: p.id,
      label: formatPermissionLabel(p),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleAddPermission = async () => {
    if (!selectedPermissionId) return;
    await createGroupPermission.mutateAsync({
      groupId,
      permissionId: selectedPermissionId,
    });
    setSelectedPermissionId(null);
    closeAdd();
  };

  const handleRemoveClick = (gpId: string, permName: string) => {
    setPermToRemove({ id: gpId, name: permName });
    openConfirm();
  };

  const confirmRemove = async () => {
    if (permToRemove) {
      await deleteGroupPermission.mutateAsync(permToRemove.id);
      closeConfirm();
      setPermToRemove(null);
    }
  };

  if (isLoading) {
    return (
      <Center py="sm">
        <Loader size="sm" />
      </Center>
    );
  }

  const sorted = [...groupPermissions].sort((a, b) =>
    (a.permission?.name || "").localeCompare(b.permission?.name || "")
  );

  return (
    <Box px="md" py="md">
      <Group justify="space-between" mb="xs">
        <Text size="sm" fw={600} c="dimmed">
          Permissions ({groupPermissions.length})
        </Text>
        <Button
          size="compact-xs"
          variant="light"
          leftSection={<IconPlus size={12} />}
          onClick={openAdd}
        >
          Add
        </Button>
      </Group>

      {sorted.length === 0 ? (
        <Text size="sm" c="dimmed" fs="italic">
          No permissions assigned to this group.
        </Text>
      ) : (
        <Flex wrap="wrap" gap={6}>
          {sorted.map((gp) => (
            <Badge
              key={gp.id}
              variant="light"
              color="blue"
              size="md"
              rightSection={
                <ActionIcon
                  size={14}
                  variant="transparent"
                  color="red"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveClick(
                      gp.id,
                      formatPermissionLabel(gp.permission)
                    );
                  }}
                >
                  <IconX size={10} />
                </ActionIcon>
              }
            >
              {formatPermissionLabel(gp.permission)}
            </Badge>
          ))}
        </Flex>
      )}

      <Modal
        opened={addOpened}
        onClose={closeAdd}
        title="Add Permission"
        size="md"
      >
        <Stack gap="md">
          <Select
            label="Permission"
            placeholder="Select a permission to add"
            data={availablePermissions}
            value={selectedPermissionId}
            onChange={setSelectedPermissionId}
            searchable
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeAdd}>
              Cancel
            </Button>
            <Button
              onClick={handleAddPermission}
              loading={createGroupPermission.isPending}
              disabled={!selectedPermissionId}
            >
              Add Permission
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={confirmOpened}
        onClose={closeConfirm}
        title="Remove Permission"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text>
            Remove <strong>{permToRemove?.name}</strong> from this group?
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeConfirm}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={confirmRemove}
              loading={deleteGroupPermission.isPending}
            >
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}

export default function GroupsAdminPage() {
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  const [editingGroup, setEditingGroup] = useState<GroupType | null>(null);
  const [groupToToggle, setGroupToToggle] = useState<GroupType | null>(null);
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<GroupFilters>({});
  const [tempFilters, setTempFilters] = useState<GroupFilters>({});
  const [page, setPage] = useState(1);

  const { data: groups = [], isLoading, refetch } = useGroupsAdmin();
  const createGroup = useCreateGroup();
  const updateGroup = useUpdateGroup();
  const toggleGroupActive = useToggleGroupActive();

  const filteredGroups = useMemo(() => {
    let result = groups;

    if (filters.status === "active") {
      result = result.filter((g) => g.isActive);
    } else if (filters.status === "inactive") {
      result = result.filter((g) => !g.isActive);
    }

    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        (group) =>
          group.name.toLowerCase().includes(searchLower) ||
          group.description?.toLowerCase().includes(searchLower)
      );
    }

    return result;
  }, [groups, debouncedSearch, filters]);

  const hasActiveFilters = filters.status && filters.status !== "all";

  const totalPages = Math.ceil(filteredGroups.length / ITEMS_PER_PAGE);
  const paginatedGroups = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredGroups.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredGroups, page]);

  const handleAdd = () => {
    setEditingGroup(null);
    openFormModal();
  };

  const handleEdit = (group: GroupType) => {
    setEditingGroup(group);
    openFormModal();
  };

  const handleSubmit = async (values: GroupFormValues) => {
    if (editingGroup) {
      await updateGroup.mutateAsync({ id: editingGroup.id, ...values });
    } else {
      await createGroup.mutateAsync(values);
    }
    closeFormModal();
  };

  const handleToggleActive = (group: GroupType) => {
    setGroupToToggle(group);
    openConfirmModal();
  };

  const confirmToggleActive = async () => {
    if (groupToToggle) {
      await toggleGroupActive.mutateAsync({
        id: groupToToggle.id,
        isActive: !groupToToggle.isActive,
      });
      closeConfirmModal();
      setGroupToToggle(null);
    }
  };

  const handleOpenFilterModal = () => {
    setTempFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...tempFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPage(1);
  };

  const handleRemoveFilter = (key: keyof GroupFilters) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
    setPage(1);
  };

  const getFilterDisplayName = (): string | null => {
    if (filters.status === "active") return "Active Only";
    if (filters.status === "inactive") return "Inactive Only";
    return null;
  };

  const toggleExpand = (groupId: string) => {
    setExpandedGroupId((prev) => (prev === groupId ? null : groupId));
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <Stack gap="lg">
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Groups</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage permission groups and organizational units. Expand a row
                to view and manage its permissions.
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          <Group align="stretch" grow>
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by name or description..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" align="center">
                    <Text size="sm" c="dimmed">
                      Filters:
                    </Text>
                    <Pill
                      withRemoveButton
                      onRemove={() => handleRemoveFilter("status")}
                    >
                      {getFilterDisplayName()}
                    </Pill>
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  Showing {filteredGroups.length} of {groups.length} groups
                </Text>
              </Stack>
            </Paper>
          </Group>

          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Groups ({filteredGroups.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(page * ITEMS_PER_PAGE, filteredGroups.length)}{" "}
                      of {filteredGroups.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredGroups.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {search || hasActiveFilters
                    ? "No groups match the current filters"
                    : "No groups found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 40 }} />
                      <Table.Th>Group Name</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th>Created</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedGroups.map((group) => {
                      const isExpanded = expandedGroupId === group.id;
                      return (
                        <>
                          <Table.Tr
                            key={group.id}
                            style={{
                              opacity: group.isActive ? 1 : 0.5,
                              cursor: "pointer",
                            }}
                            onClick={() => toggleExpand(group.id)}
                          >
                            <Table.Td>
                              {isExpanded ? (
                                <IconChevronDown size={16} />
                              ) : (
                                <IconChevronRight size={16} />
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm" fw={500}>
                                {group.name}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm" lineClamp={2}>
                                {group.description || "—"}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {new Date(group.createdAt).toLocaleDateString()}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Badge
                                color={group.isActive ? "green" : "red"}
                                variant="light"
                                size="sm"
                              >
                                {group.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Group gap="xs">
                                <Tooltip label="Edit">
                                  <ActionIcon
                                    variant="subtle"
                                    color="blue"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(group);
                                    }}
                                  >
                                    <IconEdit size={18} />
                                  </ActionIcon>
                                </Tooltip>
                                <Tooltip
                                  label={
                                    group.isActive ? "Deactivate" : "Activate"
                                  }
                                >
                                  <ActionIcon
                                    variant="subtle"
                                    color={group.isActive ? "red" : "green"}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleActive(group);
                                    }}
                                  >
                                    {group.isActive ? (
                                      <IconX size={18} />
                                    ) : (
                                      <IconCheck size={18} />
                                    )}
                                  </ActionIcon>
                                </Tooltip>
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                          {isExpanded && (
                            <Table.Tr key={`${group.id}-perms`}>
                              <Table.Td
                                colSpan={6}
                                style={{
                                  backgroundColor:
                                    "var(--mantine-color-gray-0)",
                                  padding: 0,
                                }}
                              >
                                <GroupPermissionsRow groupId={group.id} />
                              </Table.Td>
                            </Table.Tr>
                          )}
                        </>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Add/Edit Group Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={editingGroup ? "Edit Group" : "Add New Group"}
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <GroupForm
              initialValues={editingGroup || undefined}
              onSubmit={handleSubmit}
              isLoading={createGroup.isPending || updateGroup.isPending}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createGroup.isPending || updateGroup.isPending}
                onClick={() => {
                  const form = document.querySelector("form");
                  if (form) form.requestSubmit();
                }}
              >
                {editingGroup ? "Update" : "Create"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Groups"
          size="sm"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Select
              label="Status"
              placeholder="All statuses"
              data={[
                { value: "all", label: "All" },
                { value: "active", label: "Active Only" },
                { value: "inactive", label: "Inactive Only" },
              ]}
              value={tempFilters.status || "all"}
              onChange={(value) =>
                setTempFilters((prev) => ({
                  ...prev,
                  status: value as GroupFilters["status"],
                }))
              }
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeFilterModal}>
                Cancel
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Confirmation Modal for Activate/Deactivate */}
        <Modal
          opened={confirmModalOpened}
          onClose={closeConfirmModal}
          title={
            groupToToggle?.isActive ? "Deactivate Group" : "Activate Group"
          }
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {groupToToggle?.isActive ? "deactivate" : "activate"} the group{" "}
              <strong>{groupToToggle?.name}</strong>?
            </Text>
            {groupToToggle?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivated groups will no longer be available for assignment to
                users.
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeConfirmModal}>
                Cancel
              </Button>
              <Button
                color={groupToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggleActive}
                loading={toggleGroupActive.isPending}
              >
                {groupToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
